import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type OasDocument3XExtended,
  type OasWebPageApiTable,
  type OasWebPageTreeNode,
  type OasWebTab,
  isNonNullable,
  isNullable,
  isOasWebMenuNode,
  isOasWebPage,
  isOasWebPageApiTable,
  isOasWebTabsPage,
} from '@ipfabric/utils';
import type { DeviceViewElementType } from 'components/DeviceView/deviceViewTypes';
import type { MenuTreeNode, UrlListItem } from 'components/Sidebar/menuTree';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { escapeRegExp } from 'utils/escapeRegExp';
import { getXTableOas } from 'utils/oas/getXTableOas';
import { isValidNodeEndpoint } from './isValidNodeEndpoint';

const searchText = (text: string, search: string) => {
  if (!text) {
    return false;
  }
  const re = new RegExp(escapeRegExp(search), 'gi');
  const match = text.toLowerCase().match(re);
  return match && match.length > 0;
};

const findPageNodes =
  (oas: OasDocument3XExtended, deviceViewElement: DeviceViewElementType) =>
  (node: OasWebTab): node is OasWebPageApiTable => {
    const apiEndpoint = isOasWebPageApiTable(node) ? node.apiTable : null;
    return (
      isNonNullable(apiEndpoint) &&
      isValidNodeEndpoint(deviceViewElement, apiEndpoint, oas)
    );
  };

const getTreeViewNode =
  (oas: OasDocument3XExtended, search: string, path: string[]) =>
  (node: OasWebPageApiTable): UrlListItem | null => {
    const xTableOas = getXTableOas(oas, node.apiTable);
    const webPath = xTableOas?.webPath;
    const title = xTableOas?.title;
    if (webPath === undefined || title === undefined) {
      return null;
    }
    if (webPath === '' || title === '') {
      stumble(`Missing table webPath or title for ${node.apiTable}`);
      return null;
    }
    if (search) {
      const match = searchText(title ?? node.title, search);
      if (!match) {
        return null;
      }
    }
    return {
      url: webPath,
      msg: {
        pageTitle: title,
      },
      path,
    };
  };

const getTreePageNode = (
  oas: OasDocument3XExtended,
  nodes: OasWebTab[],
  search: string,
  deviceViewElement: DeviceViewElementType,
  path: string[],
): UrlListItem[] => {
  return nodes
    .filter(findPageNodes(oas, deviceViewElement))
    .map(getTreeViewNode(oas, search, path))
    .filter(isNonNullable);
};

// Get rid of nodes without children
const nodeHasDescendants = (menuTreeNode: MenuTreeNode) =>
  !!(menuTreeNode.children?.length || menuTreeNode.urlList?.length);

const getTreeNode = (
  oas: OasDocument3XExtended,
  nodes: OasWebPageTreeNode[],
  search: string,
  deviceViewElement: DeviceViewElementType,
  path: string[],
): MenuTreeNode[] => {
  return nodes.reduce(
    (acc: MenuTreeNode[], node: OasWebPageTreeNode): MenuTreeNode[] => {
      if (isOasWebMenuNode(node)) {
        acc.push({
          label: node.title,
          children: pipe(
            getTreeNode(oas, node.submenu, search, deviceViewElement, [
              ...path,
              node.title,
            ]),
            array.filter(nodeHasDescendants),
          ),
        });
        return acc;
      }
      if (isOasWebTabsPage(node)) {
        const nodeTabs = node.tabs.reduce(
          (tabsAcc: OasWebTab[], tab): OasWebTab[] => {
            if (isOasWebTabsPage(tab)) {
              tabsAcc.push(...tab.tabs);
            } else {
              tabsAcc.push(tab);
            }
            return tabsAcc;
          },
          [],
        );
        acc.push({
          label: node.title,
          urlList: getTreePageNode(oas, nodeTabs, search, deviceViewElement, [
            ...path,
            node.title,
          ]),
        });
        return acc;
        // Compatible structure with legacy table tree
      }
      if (isOasWebPage(node)) {
        acc.push({
          label: node.title,
          urlList: getTreePageNode(oas, [node], search, deviceViewElement, [
            ...path,
            node.title,
          ]),
        });
        return acc;
      }

      return acc;
    },
    [],
  );
};

export const apiTableTreeFromMenu = (
  oas: OasDocument3XExtended | null,
  search: string,
  deviceViewElement: DeviceViewElementType,
): MenuTreeNode[] => {
  const xWebPages = oas?.['x-webPages'];
  if (isNullable(xWebPages)) {
    return [];
  }
  return Object.entries(xWebPages).map(([key, nodes]): MenuTreeNode => {
    const label = `${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    return {
      label,
      children: pipe(
        getTreeNode(oas!, nodes, search, deviceViewElement, [label]),
        array.filter(nodeHasDescendants),
      ),
    };
  });
};
